import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarRange,
  CheckCircle2,
  Clock3,
  Plus,
  RefreshCcw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase';
import { getCrmErrorMessage } from '../utils/crmErrors';
import {
  calculateMinutesFromTimes,
  fetchCrmMeetings,
  formatMinutes,
  getMeetingStatusMeta,
  isProcessedMeetingStatus,
} from '../utils/crmWorkflow';

function Badge({ label, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-100 border-slate-200 text-slate-600',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
  };

  return (
    <span className={`inline-flex items-center rounded-xl border px-2.5 py-1 text-[8px] font-black uppercase tracking-widest ${tones[tone] || tones.slate}`}>
      {label}
    </span>
  );
}

function PanelTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="bg-[#0B2A36] px-4 py-3 border-b border-white/10 flex items-center gap-3 rounded-t-2xl">
      <Icon size={16} className="text-[#3FF2AA]" />
      <div className="min-w-0">
        <p className="text-[11px] font-black uppercase tracking-widest text-white truncate">{title}</p>
        {subtitle ? <p className="text-[9px] font-bold uppercase tracking-widest text-white/50">{subtitle}</p> : null}
      </div>
    </div>
  );
}

function normalizeInputTime(value) {
  return String(value || '').slice(0, 5);
}

export default function Agenda() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAgenda = useCallback(async () => {
    if (!profile?.organization_id) return;

    setLoading(true);
    try {
      const meetingRows = await fetchCrmMeetings(profile.organization_id);
      setMeetings(meetingRows);
    } catch (error) {
      console.error(error);
      toast.error(getCrmErrorMessage(error, 'No se pudo cargar la agenda CRM'), { id: 'crm-agenda-load-error' });
    } finally {
      setLoading(false);
    }
  }, [profile?.organization_id]);

  useEffect(() => {
    loadAgenda();
  }, [loadAgenda]);

  useEffect(() => {
    if (!profile?.organization_id) return;

    const channel = supabase
      .channel(`crm-meetings-${profile.organization_id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_meetings', filter: `organization_id=eq.${profile.organization_id}` }, loadAgenda)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_meeting_participants', filter: `organization_id=eq.${profile.organization_id}` }, loadAgenda)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_meeting_topics', filter: `organization_id=eq.${profile.organization_id}` }, loadAgenda)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_meeting_topic_participants', filter: `organization_id=eq.${profile.organization_id}` }, loadAgenda)
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadAgenda, profile?.organization_id]);

  const kpis = useMemo(() => ({
    total: meetings.length,
    pending: meetings.filter((meeting) => !isProcessedMeetingStatus(meeting.status)).length,
    completed: meetings.filter((meeting) => isProcessedMeetingStatus(meeting.status)).length,
  }), [meetings]);

  return (
    <div className="w-full px-3 py-3 sm:px-4 sm:py-4 lg:px-5 bg-[#F8FAFC] min-h-screen animate-in fade-in duration-500">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <CalendarRange size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black uppercase tracking-tight text-slate-800 truncate">Reuniones</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Crea la reunion, dejala pendiente y finalizala despues desde su detalle operativo.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/sistema/agenda/nueva')}
            className="px-5 py-2.5 rounded-2xl bg-[#3FF2AA] text-[#0B2A36] shadow-sm transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <Plus size={16} />
            Crear reunion
          </button>
          <button
            type="button"
            onClick={loadAgenda}
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-500 hover:text-[#51A7FD] shadow-sm transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <RefreshCcw size={16} className={loading ? 'animate-spin' : ''} />
            Sincronizar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Reuniones</p>
            <CalendarRange size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800">{kpis.total}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Base operativa</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pendientes</p>
            <Clock3 size={16} className="text-amber-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800">{kpis.pending}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Para tratar o finalizar</p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Procesadas</p>
            <CheckCircle2 size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800">{kpis.completed}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Con imputaciones ya generadas</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <PanelTitle icon={CalendarRange} title="Agenda operativa" subtitle="Entrando al detalle puedes marcar temas tratados y finalizar la reunion" />

        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left">
            <thead>
              <tr className="bg-slate-50 text-[9px] font-black uppercase tracking-widest text-slate-500">
                <th className="px-4 py-3 w-[32%]">Reunion</th>
                <th className="px-4 py-3 w-[12%]">Fecha</th>
                <th className="px-4 py-3 w-[12%]">Estado</th>
                <th className="px-4 py-3 w-[10%]">Desde</th>
                <th className="px-4 py-3 w-[10%]">Hasta</th>
                <th className="px-4 py-3 w-[12%]">Duracion</th>
                <th className="px-4 py-3 w-[12%] text-right">Accion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {meetings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-slate-400">
                    <p className="text-[10px] font-black uppercase tracking-widest">No hay reuniones registradas</p>
                  </td>
                </tr>
              ) : (
                meetings.map((meeting) => {
                  const treatedTopics = (meeting.topics || []).filter((topic) => topic.is_treated).length;
                  const calculatedDuration = calculateMinutesFromTimes(meeting.start_time, meeting.end_time);
                  const meetingStatus = getMeetingStatusMeta(meeting.status);
                  const duration = isProcessedMeetingStatus(meeting.status)
                    ? (meeting.actual_duration_minutes || calculatedDuration || meeting.duration_minutes || 0)
                    : (meeting.duration_minutes || calculatedDuration || 0);

                  return (
                    <tr
                      key={meeting.id}
                      onClick={() => navigate(`/sistema/agenda/${meeting.id}`)}
                      className="cursor-pointer hover:bg-[#F8FBFF] transition-colors"
                    >
                      <td className="px-4 py-3 align-middle">
                        <p className="text-[11px] font-black uppercase text-slate-800">{meeting.title}</p>
                        <p className="mt-1 text-[9px] font-bold uppercase tracking-widest text-slate-400">
                          {(meeting.topics || []).length} temas - {treatedTopics} tratados - {(meeting.participants || []).length} participantes
                        </p>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="text-[10px] font-black text-slate-700">{meeting.meeting_date}</span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <Badge label={meetingStatus.label} tone={meetingStatus.tone} />
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="text-[10px] font-black text-slate-700">{normalizeInputTime(meeting.start_time)}</span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className="text-[10px] font-black text-slate-700">{normalizeInputTime(meeting.end_time)}</span>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <Badge label={formatMinutes(duration)} tone={isProcessedMeetingStatus(meeting.status) ? 'emerald' : 'amber'} />
                      </td>
                      <td className="px-4 py-3 align-middle text-right">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            navigate(`/sistema/agenda/${meeting.id}`);
                          }}
                          className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#51A7FD]"
                        >
                          Ver detalle
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
